import React from "react";

import { FormControlLabel, Switch, Typography, Table, TableBody, TableRow, TableCell, Accordion, AccordionSummary, AccordionDetails, TextField, TableHead, TableContainer } from "@mui/material";
import { Box, Divider, RadioGroup, Radio, Stack, Button } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { WorkshopTestResult } from "../../types/WorkshopResult";
import { FieldType } from "../../types/Interface";
import { UnsupportedChip } from "../WSTestingStart/UnsupportedChip";
import { copyArrayWithIndexChanged } from '../../Utils';
import { WSTestingProductPairValue, WorkshopProductPairTestCaseResult } from "../../types/WSTestingData";

export interface TestCaseFilters {
    ShowPassed: boolean,
    ShowWarnings: boolean,
    ShowFailed: boolean,
    ShowNotComplete: boolean,
    ShowUnsupported: boolean
}; 

interface TestCaseGroupData {
    passedCase: boolean,
    warningCase: boolean,
    failedCase: boolean,
    notCompleteCase: boolean,
    supportedCase: boolean,
    unsupportedCase: boolean,
    groupName: string,
    testProps: WorkshopProductPairTestCaseResult[]
}

// Function to determine if a test case should be displayed based on the filters
const HaveTestCaseToDisplay = (testCaseGroup: TestCaseGroupData, filters: TestCaseFilters): boolean => {
    if (!testCaseGroup.supportedCase && !filters.ShowUnsupported) return false;
    if (testCaseGroup.passedCase && filters.ShowPassed) return true;
    if (testCaseGroup.warningCase && filters.ShowWarnings) return true;
    if (testCaseGroup.failedCase && filters.ShowFailed) return true;
    if (testCaseGroup.notCompleteCase && filters.ShowNotComplete) return true;
    if (testCaseGroup.unsupportedCase && filters.ShowUnsupported) return true; // not the same as above - it is possible to have a group of only support or unsupported
    return false;
};

const IsTestCaseToDisplay = (testCase: WorkshopProductPairTestCaseResult, filters: TestCaseFilters): boolean => {
    if (filters.ShowUnsupported && (testCase.result === WorkshopTestResult.ClientNotSupported || testCase.result === WorkshopTestResult.ServerNotSupported)) return true;
    if (filters.ShowNotComplete && testCase.result === WorkshopTestResult.NotComplete) return true;
    if (filters.ShowPassed && testCase.result === WorkshopTestResult.Passed) return true;
    if (filters.ShowWarnings && testCase.result === WorkshopTestResult.Warning) return true;
    if (filters.ShowFailed && testCase.result === WorkshopTestResult.Failed) return true;
    return false;
};

const GroupTestCaseSupport = ( testCases: WorkshopProductPairTestCaseResult[] ) => {
    let testCaseGroups: TestCaseGroupData[] = [];
    try {
        let testCaseGroupData: TestCaseGroupData;
        let index = 0;

        testCases.forEach(testCase => {
            index = testCaseGroups.findIndex(tc => tc.groupName === testCase.group);
            testCaseGroupData = testCaseGroups.find(tc => tc.groupName === testCase.group) ??
            {
                unsupportedCase: false,
                passedCase: false,
                warningCase: false,
                failedCase: false,
                notCompleteCase: false,
                supportedCase: false,
                groupName: testCase.group,
                testProps: []
            };
            if (testCase.result !== WorkshopTestResult.ClientNotSupported && testCase.result !== WorkshopTestResult.ServerNotSupported) testCaseGroupData.supportedCase = true;
            else testCaseGroupData.unsupportedCase = true;
            if (testCase.result === WorkshopTestResult.Passed) testCaseGroupData.passedCase = true;
            if (testCase.result === WorkshopTestResult.Warning) testCaseGroupData.warningCase = true;
            if (testCase.result === WorkshopTestResult.Failed) testCaseGroupData.failedCase = true;
            if (testCase.result === WorkshopTestResult.NotComplete) testCaseGroupData.notCompleteCase = true;
            testCaseGroupData.testProps.push(testCase);
            testCaseGroups = copyArrayWithIndexChanged(testCaseGroups, index, testCaseGroupData);
        })
    } catch (e) {
        console.error("Error while grouping test cases:", e);
        // Handle error appropriately, e.g., show a notification or log it
        return [] as TestCaseGroupData[]; // Return an empty array if there's an error
    }
    return testCaseGroups;
}

const defaultTestCaseFilters: TestCaseFilters = {
    ShowPassed: true,
    ShowWarnings: true,
    ShowFailed: true,
    ShowNotComplete: true,
    ShowUnsupported: false
};

// Cached outside the component so the filter selections survive WSTestcaseRecording being
// unmounted/remounted (e.g. when toggling between products that are/aren't ready to test).
let cachedTestCaseFilters: TestCaseFilters = { ...defaultTestCaseFilters };

// Maps a chip-selected WorkshopTestResult (from the tree) to a TestCaseFilters value that shows
// only that result type.
const filtersForResultType = (resultType: WorkshopTestResult): TestCaseFilters => ({
    ShowPassed: resultType === WorkshopTestResult.Passed,
    ShowWarnings: resultType === WorkshopTestResult.Warning,
    ShowFailed: resultType === WorkshopTestResult.Failed,
    ShowNotComplete: resultType === WorkshopTestResult.NotComplete,
    ShowUnsupported: resultType === WorkshopTestResult.ServerNotSupported || resultType === WorkshopTestResult.ClientNotSupported
});

export const WSTestcaseRecording = ({ productPairData, disabled, changeTestCaseResultHandler, pendingResultFilter, onPendingResultFilterConsumed }:
    {
        productPairData: WSTestingProductPairValue | null,
        disabled: boolean,
        changeTestCaseResultHandler: (tstCase: WorkshopProductPairTestCaseResult) => void,
        pendingResultFilter?: WorkshopTestResult | null,
        onPendingResultFilterConsumed?: () => void
    }) => {
    const [groupedTestCases, setGroupedTestCases] = React.useState<TestCaseGroupData[]>([]);
    const [testCaseFilters, setTestCaseFiltersState] = React.useState<TestCaseFilters>(cachedTestCaseFilters);

    const setTestCaseFilters = (filters: TestCaseFilters) => {
        cachedTestCaseFilters = filters;
        setTestCaseFiltersState(filters);
    };

    React.useEffect(() => {
        const groupedTestCasesLoc = GroupTestCaseSupport(productPairData?.testCaseResults ?? []);
        setGroupedTestCases(groupedTestCasesLoc);
    }, [productPairData]);

    // When a chip in the tree requests a specific result type be shown, apply it as the current
    // filter, then notify the parent the request has been handled so it isn't re-applied later.
    React.useEffect(() => {
        if (pendingResultFilter === null || pendingResultFilter === undefined) return;
        setTestCaseFilters(filtersForResultType(pendingResultFilter));
        onPendingResultFilterConsumed?.();
    }, [pendingResultFilter, onPendingResultFilterConsumed]);

    return <form>
        <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
            <FormControlLabel label="Show Passed" control={<Switch checked={testCaseFilters.ShowPassed} color='success'
                onChange={(event) => setTestCaseFilters({ ...testCaseFilters, ShowPassed: event.target.checked })} inputProps={{ 'aria-label': 'controlled' }} />} />

            <FormControlLabel label="Show Warnings" control={<Switch checked={testCaseFilters.ShowWarnings} color='warning'
                onChange={(event) => setTestCaseFilters({ ...testCaseFilters, ShowWarnings: event.target.checked })} inputProps={{ 'aria-label': 'controlled' }} />} />

            <FormControlLabel label="Show Failed" control={<Switch checked={testCaseFilters.ShowFailed} color='error'
                onChange={(event) => setTestCaseFilters({ ...testCaseFilters, ShowFailed: event.target.checked })} inputProps={{ 'aria-label': 'controlled' }} />} />

            <FormControlLabel label="Show Not Complete" control={<Switch checked={testCaseFilters.ShowNotComplete} color='secondary'
                onChange={(event) => setTestCaseFilters({ ...testCaseFilters, ShowNotComplete: event.target.checked })} inputProps={{ 'aria-label': 'controlled' }} />} />

            <FormControlLabel label="Show Unsupported" control={<Switch checked={testCaseFilters.ShowUnsupported} color='default'
                onChange={(event) => setTestCaseFilters({ ...testCaseFilters, ShowUnsupported: event.target.checked })} inputProps={{ 'aria-label': 'controlled' }} />} />
        </Stack>
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                    <Typography variant='bold'>{productPairData?.interfaceName ?? 'unknown'}</Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {
                    groupedTestCases.map(group => HaveTestCaseToDisplay(group, testCaseFilters) &&
                        <TestCaseGroupRecordAccordion key={group.groupName} testCaseGroupData={group} disabledForm={disabled} testCaseFilters={testCaseFilters} changeTestCaseResultHandler={changeTestCaseResultHandler} />)
                }
            </AccordionDetails>
        </Accordion>
    </form>
}

const TestCaseGroupRecordAccordion = ({ testCaseGroupData, disabledForm, testCaseFilters, changeTestCaseResultHandler }:
    {
        testCaseGroupData: TestCaseGroupData,
        disabledForm: boolean,
        testCaseFilters: TestCaseFilters,
        changeTestCaseResultHandler: (tstCase: WorkshopProductPairTestCaseResult) => void
    }) => {
    return <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{testCaseGroupData.groupName}</AccordionSummary>
        <AccordionDetails>
            <TestCaseGroupRecordDetails testCaseGroupData={testCaseGroupData} disabledForm={disabledForm}
                testCaseFilters={testCaseFilters} changeTestCaseResultHandler={changeTestCaseResultHandler}  />
        </AccordionDetails>
    </Accordion>
}

const TestCaseGroupRecordDetails = ({ testCaseGroupData, disabledForm, testCaseFilters, changeTestCaseResultHandler }:
    {
        testCaseGroupData: TestCaseGroupData,
        disabledForm: boolean,
        testCaseFilters: TestCaseFilters,
        changeTestCaseResultHandler: (tstCase: WorkshopProductPairTestCaseResult) => void
    }) => {

    return <>
        {testCaseGroupData && <fieldset className='disabledForm' disabled={disabledForm}>
            <Table className={'alternating'}>
                <TableBody>
                    {
                        testCaseGroupData.testProps.map(tc =>
                            IsTestCaseToDisplay(tc, testCaseFilters) &&
                            <TestCaseContainer key={tc.id} testCaseGroupProps={tc} changeTestCaseResultHandler={changeTestCaseResultHandler} />)
                    }
                </TableBody>
            </Table>
        </fieldset>}
    </>
}

const TestCaseContainer = ({ testCaseGroupProps, changeTestCaseResultHandler }:
    {
        testCaseGroupProps: WorkshopProductPairTestCaseResult,
        changeTestCaseResultHandler: (testCase: WorkshopProductPairTestCaseResult) => void
    }) => {
    const [expanded, setExpanded] = React.useState<boolean>(false);

    const handleAccordionChange =
        () => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded);
        };

    return <TableRow>
        <TableCell width=''>
            {testCaseGroupProps.description ?
                <AccordionDetails>
                    <Accordion defaultExpanded={false} onChange={handleAccordionChange()} sx={{ bgcolor: 'inherit' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack width={'100%'} spacing={10} direction={'row'} justifyContent={'space-between'}>
                                <Box width={'90%'}> <Typography variant='body2'>{testCaseGroupProps.name}</Typography> </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant='subscript'>{expanded ? 'Hide description' : 'Show description'}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Divider />
                            {testCaseGroupProps.description}
                            <Divider />
                        </AccordionDetails>
                    </Accordion>
                </AccordionDetails>
                :
                testCaseGroupProps.name}
        </TableCell>
        <TableCell width='50%' padding='none' sx={{ borderLeft: '1px solid', borderColor: '#e1e9ee' }}>
            {<TestingResultInput testCaseGroupProps={testCaseGroupProps} changeHandler={changeTestCaseResultHandler} />}
        </TableCell>
    </TableRow>
}

const TestingResultInput = ({ testCaseGroupProps, changeHandler }:
    {
        testCaseGroupProps: WorkshopProductPairTestCaseResult,
        changeHandler: (testCase: WorkshopProductPairTestCaseResult) => void
    }) => {
    let resultRadio = <RadioGroupInput testCase={testCaseGroupProps} changeHandler={changeHandler} />
    let optionalResultInput = <></>

    const commentDisabled = testCaseGroupProps.result === WorkshopTestResult.ServerNotSupported ||
        testCaseGroupProps.result === WorkshopTestResult.ClientNotSupported;

    const commentOnChangeHandler = (comment: string) => {
        changeHandler({ ...testCaseGroupProps, comment: comment ?? '' })
    }

    if (testCaseGroupProps.result === WorkshopTestResult.ServerNotSupported)
        resultRadio = <UnsupportedChip server={true} />
    else if (testCaseGroupProps.result === WorkshopTestResult.ClientNotSupported)
        resultRadio = <UnsupportedChip server={false} />
    else {
        switch (testCaseGroupProps.fieldTypeVal) {
            case FieldType.Table: optionalResultInput = <TableInput testCase={testCaseGroupProps} /> // changeHandler={changeHandler}
                break;
        }
    }

    return <Stack direction='column'>
        <Stack direction='row' justifyContent={'space-evenly'} padding={'5px'} paddingLeft={'10px'}>
            {resultRadio}
        </Stack>
        <CommentField testCase={testCaseGroupProps} disabled={commentDisabled} commentOnChangeHandler={commentOnChangeHandler} />
        {optionalResultInput}
    </Stack>
}

const RadioGroupInput = ({ testCase, changeHandler }:
    {
        testCase: WorkshopProductPairTestCaseResult,
        changeHandler: (testCase: WorkshopProductPairTestCaseResult) => void
    }) => {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        // If Reset selected, set result to NotComplete and the parent will update the model.
        if (newValue === 'reset') {
            changeHandler({ ...testCase, result: WorkshopTestResult.NotComplete } as WorkshopProductPairTestCaseResult);
            return;
        }

        changeHandler({ ...testCase, result: Number(newValue) } as WorkshopProductPairTestCaseResult)
    };

    return <RadioGroup row={true} sx={{ flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'inherit' }} value={testCase?.result ?? 1337} //dummy, so value is not undefined during the first render
        onChange={handleChange}>
        <FormControlLabel value={WorkshopTestResult.Passed} sx={{ flexShrink: 1 }}
            control={<Radio sx={{ '&.Mui-checked': { color: 'green' } }} />}
            label={<Typography variant='body2' sx={testCase?.result === WorkshopTestResult.Passed ? { color: 'green' } : {}}>{"Passed"}</Typography>} />

        <FormControlLabel value={WorkshopTestResult.Warning} sx={{ flexShrink: 1 }}
            control={<Radio sx={{ '&.Mui-checked': { color: 'orange' } }} />}
            label={<Typography variant='body2' sx={testCase?.result === WorkshopTestResult.Warning ? { color: 'orange' } : {}}>{"Warning"}</Typography>} />

        <FormControlLabel value={WorkshopTestResult.Failed} sx={{ flexShrink: 1 }}
            control={<Radio sx={{ '&.Mui-checked': { color: 'red' } }} />}
            label={<Typography variant='body2' sx={testCase?.result === WorkshopTestResult.Failed ? { color: 'red' } : {}}>{"Failed"}</Typography>} />

        {/* Minimal-change Reset: when clicked, set model to NotComplete. Parent update makes value 0 so no radio is selected. */}
        <FormControlLabel value={'reset'} sx={{ flexShrink: 1 }}
            control={<Radio />}
            label={<Typography variant='body2'>{"Reset"}</Typography>} />
    </RadioGroup>
}

const CommentField = ({ testCase, disabled, commentOnChangeHandler }:
    {
        testCase: WorkshopProductPairTestCaseResult,
        disabled: boolean,
        commentOnChangeHandler: (value: string) => void
    }) => {
    const [textFieldContents, setTextFieldContents] = React.useState('');
    React.useEffect(() => {
        setTextFieldContents(testCase.comment ?? '');
    }, [testCase]);

    return <TextField fullWidth sx={{ flexGrow: 1, display: textFieldContents === "" && disabled ? "none" : "inline" }}
        label='Comment' disabled={disabled} multiline value={textFieldContents}
        onChange={e => setTextFieldContents(e.target.value)} onBlur={e => commentOnChangeHandler(textFieldContents)} />
}

const TableInput = ({ testCase }:
    {
        testCase: WorkshopProductPairTestCaseResult,
    }) => {
    let columnNames;
    let tableRows;

    try {
        columnNames = JSON.parse(testCase.fieldSchema)['columns'];
        tableRows = JSON.parse(testCase?.value ?? '[""]');
    } catch (e) {
        return <>{(e as SyntaxError).message}</>;
    }

    return <TableContainer>
        <InputTable columnNames={columnNames} rows={tableRows} />
        <Button onClick={() => { }}>Add row</Button>
    </TableContainer>
}

const InputTable = ({ columnNames, rows }: { columnNames?: [string], rows?: [[string]] }) => {
    return <Table>
        <TableHead>
            <TableRow>
                {columnNames?.map(columnName => <TableCell>{columnName}</TableCell>)}
            </TableRow>
        </TableHead>
        <TableBody>
            {rows && rows?.map(row => <TableRow>{row.map?.(column => <TableCell>{column}</TableCell>)} </TableRow>)}
        </TableBody>
    </Table>
}
